import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Filter, FilterList, Flex } from '@percent/lemonade'
import { DisbursementsFiltersProps } from './DisbursementsFilters.types'
import { useFoundations } from '@percent/admin-dashboard/common/hooks'
import { FoundationNameWithFlag } from '@percent/admin-dashboard/common/components'
import { DisbursementsDateRangeFilter } from './DisbursementsDateRangeFilter'
import {
  ACTION_REQUIRED_STATUSES,
  ALL_FOUNDATIONS_VALUE,
  NO_ACTION_REQUIRED_STATUSES,
  PAID_BY_FILTER_TYPE,
  PAID_BY_LABEL_KEY,
  PAID_BY_ORDER,
  PAID_BY_TO_DISBURSEMENT_TYPE,
  PaidByFilterType,
  STATUS_FILTER_TYPE,
  STATUS_LABEL_KEY,
  STATUS_ORDER,
  StatusFilterType
} from './disbursementsFilterOptions'

export function DisbursementsFilters({ queryParams, setQueryParams }: DisbursementsFiltersProps) {
  const { t } = useTranslation()
  const { foundations } = useFoundations()

  // Flags rather than bare names: foundations of the same name exist in different countries, which
  // is why the Foundation column carries one per row too. Single-select until the list endpoint
  // accepts an array — getDisbursementBatchesListQueryValidator declares one scalar `foundationId`.
  const foundationOptions = useMemo(
    () => [
      { label: t('typography.all'), value: ALL_FOUNDATIONS_VALUE },
      ...Object.values(foundations ?? {}).map(({ id, name, countryCode }) => ({
        label: <FoundationNameWithFlag countryCode={countryCode} name={name} />,
        value: id
      }))
    ],
    [foundations, t]
  )

  // Ordered here, labelled from the shared keys so the chips summarising these filters cannot drift
  // from the options themselves.
  const paidByOptions = useMemo(
    () =>
      PAID_BY_ORDER.map(value => ({
        label: t(PAID_BY_LABEL_KEY[value]),
        value
      })),
    [t]
  )

  const statusOptions = useMemo(
    () =>
      STATUS_ORDER.map(value => ({
        label: t(STATUS_LABEL_KEY[value]),
        value
      })),
    [t]
  )

  const selectedPaidBy = useMemo(() => {
    const selectedTypes = queryParams.anyDisbursementsWithTypes ?? []

    return Object.values(PAID_BY_FILTER_TYPE).filter(option =>
      selectedTypes.includes(PAID_BY_TO_DISBURSEMENT_TYPE[option])
    )
  }, [queryParams.anyDisbursementsWithTypes])

  const selectedStatuses = useMemo(
    () => [
      ...(queryParams.anyDisbursementsWithStatus?.length ? [STATUS_FILTER_TYPE.ACTION_REQUIRED] : []),
      ...(queryParams.allDisbursementsWithStatus?.length ? [STATUS_FILTER_TYPE.NO_ACTION_REQUIRED] : [])
    ],
    [queryParams.allDisbursementsWithStatus, queryParams.anyDisbursementsWithStatus]
  )

  const handlePaidByChange = (values: string[]) => {
    const disbursementTypes = values.map(value => PAID_BY_TO_DISBURSEMENT_TYPE[value as PaidByFilterType])

    setQueryParams({
      ...queryParams,
      anyDisbursementsWithTypes: disbursementTypes.length ? disbursementTypes : undefined,
      allDisbursementsWithTypes: undefined
    })
  }

  const handleStatusChange = (values: string[]) => {
    // The API ANDs the two status params, so selecting both would match no batch at all. Picking
    // one switches away from the other rather than leaving that unsatisfiable pair.
    if (!values.length) {
      setQueryParams({ ...queryParams, anyDisbursementsWithStatus: undefined, allDisbursementsWithStatus: undefined })

      return
    }

    // FilterList reports the whole selection, so exactly one value here is newly added.
    const added = values.find(value => !selectedStatuses.includes(value as StatusFilterType))
    const selected = added ?? values[0]

    setQueryParams({
      ...queryParams,
      anyDisbursementsWithStatus:
        selected === STATUS_FILTER_TYPE.ACTION_REQUIRED ? ACTION_REQUIRED_STATUSES : undefined,
      allDisbursementsWithStatus:
        selected === STATUS_FILTER_TYPE.NO_ACTION_REQUIRED ? NO_ACTION_REQUIRED_STATUSES : undefined
    })
  }

  const handleFoundationChange = (value: string) => {
    setQueryParams({ ...queryParams, foundationId: value || undefined })
  }

  return (
    <Flex flex="0 0 auto" align="center" gap={4}>
      <Filter
        label={t('dropdown.foundation')}
        options={foundationOptions}
        selectedValue={queryParams.foundationId ?? ALL_FOUNDATIONS_VALUE}
        onChange={handleFoundationChange}
        testId="disbursements-foundation-filter"
        compact
      />
      <FilterList
        label={t('table.paidBy')}
        options={paidByOptions}
        selectedValues={selectedPaidBy}
        onChange={handlePaidByChange}
        testId="disbursements-paid-by-filter"
      />
      <FilterList
        label={t('dropdown.status.title')}
        options={statusOptions}
        selectedValues={selectedStatuses}
        onChange={handleStatusChange}
        testId="disbursements-status-filter"
      />
      <DisbursementsDateRangeFilter
        label={t('table.createdDate')}
        from={queryParams.createdAtFrom}
        to={queryParams.createdAtTo}
        onChange={({ from, to }) => setQueryParams({ ...queryParams, createdAtFrom: from, createdAtTo: to })}
        testId="disbursements-created-date-filter"
      />
      <DisbursementsDateRangeFilter
        label={t('table.upTo')}
        from={queryParams.endDateFrom}
        to={queryParams.endDateTo}
        onChange={({ from, to }) => setQueryParams({ ...queryParams, endDateFrom: from, endDateTo: to })}
        testId="disbursements-up-to-filter"
      />
    </Flex>
  )
}
